import type { GetStaticPropsContext, GetStaticPropsResult } from 'next';
import Cookies from 'js-cookie';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { NextSeo } from 'next-seo';
import router, { useRouter } from 'next/router';
import React, { ReactNode, useState } from 'react';

import { useLayoutEffect } from '@hooks/useIsomorphicLayoutEffect';
import getAllPromoTickerData from '@adapters/prismic/fetching/promos/getAllPromoTickerData';
import getBannerStripGlobalData from '@adapters/prismic/fetching/banner-strip-global/getBannerStripGlobalData';
import { PromoTicker } from '@adapters/prismic/types/promoTicker';

import { AccountTabs } from '@components/account/types';
import SignUpForm from '@components/auth/SignUpForm';
import Breadcrumbs from '@components/global/Breadcrumbs';
import Container from '@components/global/Container';
import Layout from '@components/global/Layout';
import Heading from '@components/ui/Heading';

import {
  FEATURE_FLAG_LOYALTY,
  FEATURE_FLAG_PROMO_BAR_SERVER_SIDE,
  loyaltyFFCookieName,
} from '@config/feature-flag';
import { useCartUI } from '@context/CartContext';

import { CHECKOUT_STATUSES } from '@adapters/bigcommerce/constants';
import { getAllStoresDataForSetMyStore } from '@adapters/prismic/fetching/stores';
import { MyStoreData } from '@adapters/prismic/fetching/stores/types';

import { buildCheckoutCustomerStatusQuery } from '@utils/analytics/buildCheckoutCustomerStatusQuery';
import { MEC_TITLE } from '@utils/constants';
import { GA4_EVENT_NAMES, trackCartViewEvent } from '@utils/gtm/ga4';
import checkoutAnalytics from '@utils/gtm/ga4/checkout';
import { altLocale } from '@utils/locale';
import { localeAltPathAbs, localePathAbs } from '@utils/url';
import { getPreviewRef } from '@adapters/prismic/utils/getPreviewRef';

import styles from './register.module.scss';
import getPromoBarData from '@adapters/prismic/fetching/promos/getPromoBarData';

const { CREATE_ACCOUNT_AT_CHECKOUT } = GA4_EVENT_NAMES;

interface SignUpPageProps {
  canonicalUrl: string;
  langAltUrl: string;
  promoMessages?: PromoTicker[] | null;
  xDefault: string;
}

interface MECStores {
  stores: MyStoreData[] | null;
}

export async function getStaticPaths() {
  return {
    paths: [{ params: { locale: 'en' } }, { params: { locale: 'fr' } }],
    fallback: false,
  };
}

export async function getStaticProps({
  params,
  previewData,
  preview = false,
}: GetStaticPropsContext): Promise<GetStaticPropsResult<
  SignUpPageProps | MECStores
> | null> {
  const locale = params?.locale === 'fr' ? 'fr' : 'en';
  if (!locale) {
    throw new Error('No locale specified for page');
  }

  const previewRef = getPreviewRef(preview, previewData);

  const [
    stores,
    promoMessages,
    bannerStripData,
    serverSideTranslation,
    promoBarData,
  ] = await Promise.allSettled([
    getAllStoresDataForSetMyStore(locale),
    !FEATURE_FLAG_PROMO_BAR_SERVER_SIDE &&
      locale &&
      getAllPromoTickerData(locale),
    getBannerStripGlobalData(locale, preview ? previewRef : undefined),
    serverSideTranslations(locale, ['common', 'auth']),
    FEATURE_FLAG_PROMO_BAR_SERVER_SIDE && locale && getPromoBarData(locale),
  ]).then((results) =>
    results.map((promise) => {
      if (
        promise.status === 'rejected' ||
        typeof promise.value === 'undefined'
      ) {
        console.error('promise rejected or returning undefined', promise);
        return null;
      }
      return promise.value;
    })
  );

  const canonicalUrl = localePathAbs(locale, `/register`) ?? '';
  const langAltUrl = localeAltPathAbs(locale, `/register`) ?? '';
  const xDefault = locale === 'en' ? canonicalUrl : langAltUrl;

  return {
    props: {
      stores,
      canonicalUrl,
      langAltUrl,
      xDefault,
      ...serverSideTranslation,
      promoMessages,
      promoBarData,
      bannerStripData,
    },
  };
}

export default function SignUpPage({
  canonicalUrl,
  langAltUrl,
  xDefault,
}: SignUpPageProps): ReactNode {
  const { query } = useRouter();
  const locale = (query.locale as string) || 'en';
  const { t } = useTranslation('common');

  const { cart } = useCartUI();

  const description = t('signup.description');
  const title = t('signup.heading');

  const [loyaltyFeatureFlag, setLoyaltyFeatureFlag] =
    useState(FEATURE_FLAG_LOYALTY);

  useLayoutEffect(() => {
    const loyaltyCookie = Cookies.get(loyaltyFFCookieName);

    if (loyaltyCookie === 'true') {
      setLoyaltyFeatureFlag(true);
    }
  }, []);

  const onSignUp = () => {
    trackCartViewEvent({
      eventName: CREATE_ACCOUNT_AT_CHECKOUT,
      cart,
    });

    if (query?.redirect === 'checkout') {
      checkoutAnalytics.accountCreationBeforeCheckout();
      const queryWithMemberStatus = buildCheckoutCustomerStatusQuery({
        status: CHECKOUT_STATUSES.NEW_MEMBER,
        query,
        excludeKeys: ['redirect'],
      });

      window.location.href = `/checkout${queryWithMemberStatus}`;
    } else {
      checkoutAnalytics.accountCreationFromStorefront();
      if (query?.redirect === AccountTabs.MemberRewards && loyaltyFeatureFlag) {
        router.push(`/my-account?p=${AccountTabs.MemberRewards}`);
        window.location.href = `/my-account?p=${AccountTabs.MemberRewards}`;
        return;
      }
      router.push('/');
      window.location.href = '/';
    }
  };

  return (
    <>
      <NextSeo
        noindex
        description={description}
        title={`${title} ${MEC_TITLE}`}
        canonical={canonicalUrl}
        languageAlternates={[
          { hrefLang: altLocale(locale), href: langAltUrl },
          { hrefLang: String(locale), href: canonicalUrl },
          { hrefLang: 'x-default', href: xDefault },
        ]}
        openGraph={{
          title,
          description,
          locale,
        }}
      />

      <Container>
        <Breadcrumbs name={t('signup.heading')} breadcrumbs={[]} />
        <div className={styles.registerContainer}>
          <Heading styleLevel="3" semanticLevel="h1">
            {loyaltyFeatureFlag
              ? t('signup.heading.loyalty')
              : t('signup.heading')}
          </Heading>
          <p className={styles.cta}>
            {loyaltyFeatureFlag ? t('signup.cta.loyalty') : t('signup.cta')}
          </p>
          <SignUpForm
            onSignUp={onSignUp}
            submitText="signup.create.account"
            existingError="signup.account.exists"
            isCheckoutRedirect={query?.redirect === 'checkout' || false}
          />
        </div>
      </Container>
    </>
  );
}

SignUpPage.Layout = Layout;
